import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { LIMITS } from '@/config';
import { useAuthContext } from '@/features/auth/AuthContext';
import { UnifiedDiffView } from '@/features/diff/UnifiedDiffView';
import type { ProposalResponse } from '@/features/propose/api';
import type { DictKey } from '@/i18n/en';
import { useT } from '@/i18n/LocaleProvider';

import {
  createModerationApiClient,
  type ProposalApprovalResponse,
  type ProposalDiffResponse,
} from './api';
import { hasModeratorScope } from './permissions';

export type ModerationDashboardProps = {
  lang?: string;
  selectedProposalID?: string | null;
  onLogin?: () => void;
  onRegister?: () => void;
  onSelectProposal?: (proposalID: string | null) => void;
  onAuditLog?: () => void;
};

type DecisionMode = 'approve' | 'reject';

type DecisionNotice = {
  tone: 'success';
  message: string;
};

type TFn = (key: DictKey, vars?: Record<string, string>) => string;

export function ModerationDashboard({
  lang,
  selectedProposalID,
  onLogin,
  onRegister,
  onSelectProposal,
  onAuditLog,
}: ModerationDashboardProps) {
  const t = useT();
  const auth = useAuthContext();
  const canModerate = hasModeratorScope(auth.account);
  const api = useMemo(() => createModerationApiClient(() => auth.token), [auth.token]);
  const [proposals, setProposals] = useState<ProposalResponse[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [diff, setDiff] = useState<ProposalDiffResponse | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [decisionNotice, setDecisionNotice] = useState<DecisionNotice | null>(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingDiff, setIsLoadingDiff] = useState(false);
  const [decisionMode, setDecisionMode] = useState<DecisionMode | null>(null);

  const activeProposalID = selectedProposalID ?? proposals[0]?.id ?? null;
  const activeProposal = proposals.find((proposal) => proposal.id === activeProposalID) ?? null;

  useEffect(() => {
    if (!canModerate) {
      setProposals([]);
      setNextCursor(null);
      setListError(null);
      setDiff(null);
      return;
    }

    let cancelled = false;

    async function loadPendingProposals() {
      setIsLoadingList(true);
      setListError(null);

      try {
        const result = await api.listPendingProposals({ limit: LIMITS.proposalLimit, lang });

        if (cancelled) {
          return;
        }

        setProposals(result.proposals);
        setNextCursor(result.next_cursor ?? null);
      } catch (error) {
        if (!cancelled) {
          if (isUnauthorizedError(error)) {
            auth.onUnauthorized();
            return;
          }

          setListError(errorMessage(error, t('moderation_error_load')));
        }
      } finally {
        if (!cancelled) {
          setIsLoadingList(false);
        }
      }
    }

    void loadPendingProposals();

    return () => {
      cancelled = true;
    };
  }, [api, canModerate, auth.onUnauthorized, t, lang]);

  useEffect(() => {
    if (!canModerate || !activeProposalID) {
      setDiff(null);
      setDiffError(null);
      setIsLoadingDiff(false);
      return;
    }

    let cancelled = false;

    async function loadDiff() {
      setDiff(null);
      setDiffError(null);
      setIsLoadingDiff(true);

      try {
        const result = await api.getProposalDiff(activeProposalID);

        if (!cancelled) {
          setDiff(result);
        }
      } catch (error) {
        if (!cancelled) {
          if (isUnauthorizedError(error)) {
            auth.onUnauthorized();
            return;
          }

          setDiffError(errorMessage(error, t('moderation_error_diff')));
        }
      } finally {
        if (!cancelled) {
          setIsLoadingDiff(false);
        }
      }
    }

    void loadDiff();

    return () => {
      cancelled = true;
    };
  }, [activeProposalID, api, canModerate, auth.onUnauthorized, t]);

  async function loadMore() {
    if (!nextCursor || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);
    setListError(null);

    try {
      const result = await api.listPendingProposals({
        limit: LIMITS.proposalLimit,
        cursor: nextCursor,
        lang,
      });

      setProposals((current) => appendUniqueProposals(current, result.proposals));
      setNextCursor(result.next_cursor ?? null);
    } catch (error) {
      if (isUnauthorizedError(error)) {
        auth.onUnauthorized();
        return;
      }

      setListError(errorMessage(error, t('moderation_error_load_more')));
    } finally {
      setIsLoadingMore(false);
    }
  }

  async function decide(mode: DecisionMode) {
    if (!activeProposalID || decisionMode) {
      return;
    }

    const actionLabel = mode === 'approve' ? t('moderation_approve') : t('moderation_reject');
    const label = proposalLabel(activeProposal, activeProposalID);
    const confirmed = window.confirm(t('moderation_confirm', { action: actionLabel, label }));

    if (!confirmed) {
      return;
    }

    const nextProposalID = nextProposalAfter(proposals, activeProposalID);

    setDecisionMode(mode);
    setDecisionError(null);
    setDecisionNotice(null);

    try {
      if (mode === 'approve') {
        const result = await api.approveProposal(activeProposalID);
        setDecisionNotice({
          tone: 'success',
          message: approvalMessage(result, t),
        });
      } else {
        const result = await api.rejectProposal(activeProposalID);
        setDecisionNotice({
          tone: 'success',
          message: t('moderation_rejected', { label: proposalLabel(result, result.id) }),
        });
      }

      setProposals((current) => current.filter((proposal) => proposal.id !== activeProposalID));
      onSelectProposal?.(nextProposalID);
    } catch (error) {
      if (isUnauthorizedError(error)) {
        setDecisionError(t('error_session_expired'));
        auth.onUnauthorized();
        return;
      }

      setDecisionError(errorMessage(error, t('moderation_error_decision')));
    } finally {
      setDecisionMode(null);
    }
  }

  if (!auth.isAuthenticated) {
    return (
      <section className="moderation-workspace" aria-labelledby="moderation-title">
        <ModerationHeader />
        <div className="auth-required">
          <strong>{t('moderation_moderator_required')}</strong>
          <span>{t('moderation_moderator_hint')}</span>
          <div className="form-actions">
            <button className="primary-action" type="button" onClick={onLogin}>
              {t('auth_login')}
            </button>
            <button className="text-action" type="button" onClick={onRegister}>
              {t('auth_register')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!canModerate) {
    return (
      <section className="moderation-workspace" aria-labelledby="moderation-title">
        <ModerationHeader />
        <div className="auth-required" data-testid="moderation-forbidden">
          <strong>{t('moderation_forbidden')}</strong>
          <span>{t('moderation_forbidden_hint')}</span>
        </div>
      </section>
    );
  }

  return (
    <section className="moderation-workspace" aria-labelledby="moderation-title">
      <ModerationHeader
        action={
          <>
            <button type="button" onClick={() => onSelectProposal?.(null)}>
              {t('moderation_queue_label')}
            </button>
            {onAuditLog ? (
              <button type="button" onClick={onAuditLog}>
                {t('audit_log_title')}
              </button>
            ) : null}
          </>
        }
      />

      {decisionNotice ? (
        <div className={`decision-notice is-${decisionNotice.tone}`} data-testid="decision-outcome">
          {decisionNotice.message}
        </div>
      ) : null}

      <div className="moderation-layout">
        <aside className="moderation-queue" aria-label={t('moderation_queue_label')}>
          <div className="moderation-queue-header">
            <h2>{t('moderation_pending_label')}</h2>
            <span>{proposals.length}</span>
          </div>

          {listError ? <div className="form-error">{listError}</div> : null}

          {isLoadingList && proposals.length === 0 ? (
            <div className="empty-note">{t('moderation_queue_loading')}</div>
          ) : null}

          {!isLoadingList && proposals.length === 0 ? (
            <div className="empty-note">{t('moderation_no_proposals')}</div>
          ) : null}

          {proposals.length > 0 ? (
            <ul className="moderation-queue-list">
              {proposals.map((proposal) => (
                <li key={proposal.id}>
                  <button
                    className={`moderation-queue-item${
                      proposal.id === activeProposalID ? ' is-active' : ''
                    }`}
                    type="button"
                    onClick={() => onSelectProposal?.(proposal.id)}
                  >
                    <span>{proposalKindLabel(proposal, t)}</span>
                    <strong>{proposalLabel(proposal, proposal.id)}</strong>
                    <small>{proposalMeta(proposal)}</small>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {nextCursor ? (
            <button
              className="text-action moderation-load-more"
              type="button"
              disabled={isLoadingMore}
              onClick={() => void loadMore()}
            >
              {isLoadingMore ? t('moderation_load_more_loading') : t('moderation_load_more')}
            </button>
          ) : null}
        </aside>

        <section className="moderation-review" aria-labelledby="moderation-review-title">
          {!activeProposalID ? (
            <div className="empty-note">{t('moderation_select')}</div>
          ) : (
            <>
              <header className="moderation-review-header">
                <div>
                  <p>
                    {activeProposal
                      ? proposalKindLabel(activeProposal, t)
                      : t('moderation_proposal_label')}
                  </p>
                  <h2 id="moderation-review-title">
                    {proposalLabel(activeProposal, activeProposalID)}
                  </h2>
                  <div className="moderation-review-meta">
                    <span>{activeProposalID}</span>
                    {activeProposal?.language ? (
                      <span title={t('moderation_meta_lang')}>{activeProposal.language}</span>
                    ) : null}
                    {activeProposal?.translation_group ? (
                      <span title={t('moderation_meta_group')}>
                        {activeProposal.translation_group}
                      </span>
                    ) : null}
                    {activeProposal?.base_revision ? (
                      <span>{activeProposal.base_revision}</span>
                    ) : null}
                    {activeProposal?.status ? <span>{activeProposal.status}</span> : null}
                  </div>
                </div>

                <div className="moderation-review-actions">
                  <button
                    className="primary-action"
                    type="button"
                    disabled={Boolean(decisionMode)}
                    onClick={() => void decide('approve')}
                  >
                    {decisionMode === 'approve'
                      ? t('moderation_approve_loading')
                      : t('moderation_approve')}
                  </button>
                  <button
                    className="danger-action"
                    type="button"
                    disabled={Boolean(decisionMode)}
                    onClick={() => void decide('reject')}
                  >
                    {decisionMode === 'reject'
                      ? t('moderation_reject_loading')
                      : t('moderation_reject')}
                  </button>
                </div>
              </header>

              {decisionError ? <div className="form-error">{decisionError}</div> : null}
              {diffError ? <div className="form-error">{diffError}</div> : null}
              {isLoadingDiff ? (
                <div className="empty-note">{t('moderation_diff_loading')}</div>
              ) : null}
              {diff ? <UnifiedDiffView hunks={diff.hunks} /> : null}
            </>
          )}
        </section>
      </div>
    </section>
  );
}

function ModerationHeader({ action }: { action?: ReactNode }) {
  const t = useT();

  return (
    <header className="editor-header moderation-header">
      <div>
        <p>{t('moderation_title')}</p>
        <h1 id="moderation-title">{t('moderation_queue_label')}</h1>
      </div>

      {action ? <div className="editor-header-actions">{action}</div> : null}
    </header>
  );
}

function isUnauthorizedError(error: unknown): boolean {
  return (
    Boolean(error) &&
    typeof error === 'object' &&
    'status' in (error as object) &&
    (error as { status: number }).status === 401
  );
}

function appendUniqueProposals(
  current: ProposalResponse[],
  incoming: ProposalResponse[],
): ProposalResponse[] {
  const seen = new Set(current.map((proposal) => proposal.id));
  const next = [...current];

  for (const proposal of incoming) {
    if (seen.has(proposal.id)) {
      continue;
    }

    seen.add(proposal.id);
    next.push(proposal);
  }

  return next;
}

function nextProposalAfter(proposals: ProposalResponse[], activeProposalID: string): string | null {
  const index = proposals.findIndex((proposal) => proposal.id === activeProposalID);

  if (index < 0) {
    return proposals[0]?.id ?? null;
  }

  return proposals[index + 1]?.id ?? proposals[index - 1]?.id ?? null;
}

function proposalLabel(proposal: ProposalResponse | null | undefined, fallback: string): string {
  if (!proposal) {
    return fallback;
  }

  if (proposal.kind === 'new') {
    return proposal.title || proposal.slug || fallback;
  }

  // Edit proposals carry the target page's title (denormalized) for display.
  return proposal.title || proposal.summary || proposal.page_id || fallback;
}

function proposalKindLabel(proposal: ProposalResponse, t: TFn): string {
  return proposal.kind === 'new' ? t('moderation_kind_new') : t('moderation_kind_edit');
}

function proposalMeta(proposal: ProposalResponse): string {
  const target = proposal.kind === 'new' ? proposal.slug || proposal.title : proposal.page_id;
  const lang = proposal.language;
  const createdAt = formatDate(proposal.created_at);

  return [target, lang, createdAt].filter(Boolean).join(' · ');
}

function approvalMessage(result: ProposalApprovalResponse, t: TFn): string {
  const label = proposalLabel(result.proposal, result.proposal.id);
  const revisionPart = result.revision.id
    ? t('moderation_approved_revision', { id: result.revision.id })
    : '';

  return `${t('moderation_approved', { label })}${revisionPart}`;
}

function formatDate(value: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}
